// Basic set of commands.
import { addItems, addItemsAtIndices, addToGroup, removeFromGroup, removeItems, updateAppearance, updateAppearanceProperties, updateItemsAppearance, updateItemsAppearanceProperties } from "store/actions";
import appStore, { GroupsOperationData } from "store/store";
/** Changes the selection state of several items. */
export class DeselectAndSelect {
    constructor(ids) {
        var _a, _b;
        this.selectIds = (_a = ids.select) !== null && _a !== void 0 ? _a : [];
        this.deselectIds = (_b = ids.deselect) !== null && _b !== void 0 ? _b : [];
    }
    execute() {
        appStore.dispatchFn("SelectDeselect", DeselectAndSelect.mutator(this.selectIds, this.deselectIds));
    }
    undo() {
        // Swap select and deselect around.
        appStore.dispatchFn("SelectDeselect", DeselectAndSelect.mutator(this.deselectIds, this.selectIds));
    }
    static mutator(select, deselect) {
        return (state) => {
            var newState = Object.assign({}, state);
            newState.items = state.items.map(a => {
                if (select.indexOf(a.id) > -1) {
                    return Object.assign(Object.assign({}, a), { selected: true });
                }
                else if (deselect.indexOf(a.id) > -1) {
                    return Object.assign(Object.assign({}, a), { selected: false });
                }
                return a;
            });
            newState.arrows = state.arrows.map(a => {
                if (select.indexOf(a.id) > -1) {
                    return Object.assign(Object.assign({}, a), { selected: true });
                }
                else if (deselect.indexOf(a.id) > -1) {
                    return Object.assign(Object.assign({}, a), { selected: false });
                }
                return a;
            });
            return newState;
        };
    }
}
// Changes the selection state of apparatus to selected.
// Currently assumes apparatus ID.
export class Select extends DeselectAndSelect {
    constructor(ids) {
        super({ select: ids });
    }
}
export class Deselect extends DeselectAndSelect {
    constructor(ids) {
        super({ deselect: ids });
    }
}
export class Flip {
    constructor(apparatus, opts) {
        this.apparatus = apparatus;
        this.oldRotation = apparatus.rotation;
        if (opts) {
            this.newRotation = opts.vertical ? 180 - this.oldRotation : -this.oldRotation;
        }
        else {
            this.newRotation = this.oldRotation;
        }
    }
    execute() {
        appStore.dispatchFn("Flip", store => (Object.assign(Object.assign({}, store), { items: store.items.map(i => i.id == this.apparatus.id ? Object.assign(Object.assign({}, i), { flipped: !this.apparatus.flipped, rotation: this.newRotation }) : i) })));
    }
    undo() {
        appStore.dispatchFn("Undo-Flip", store => (Object.assign(Object.assign({}, store), { items: store.items.map(i => i.id == this.apparatus.id ? Object.assign(Object.assign({}, i), { flipped: this.apparatus.flipped, rotation: this.oldRotation }) : i) })));
    }
}
export class AddApparatus {
    constructor(apparatuses) {
        this.apparatuses = apparatuses;
        this.groupInformation = new GroupsOperationData();
        for (let apparatus of apparatuses) {
            if (apparatus.parentGroup) {
                this.groupInformation.add(apparatus.parentGroup, apparatus.id);
            }
        }
    }
    execute() {
        if (this.groupInformation.hasData()) {
            appStore.dispatch(addToGroup(this.groupInformation));
        }
        appStore.dispatch(addItems(this.apparatuses));
    }
    undo() {
        appStore.dispatch(removeItems(this.apparatuses.map(a => a.id)));
        if (this.groupInformation.hasData()) {
            appStore.dispatch(removeFromGroup(this.groupInformation));
        }
    }
}
export class DeleteApparatus {
    constructor(apparatuses) {
        this.apparatuses = apparatuses;
        this.groupInformation = new GroupsOperationData();
        this.itemsWithIndex = [];
        for (let apparatus of apparatuses) {
            if (apparatus.parentGroup) {
                this.groupInformation.remove(apparatus.parentGroup, apparatus.id);
            }
            this.itemsWithIndex.push({ item: apparatus, index: appStore.items().indexOf(apparatus) });
        }
    }
    execute() {
        appStore.dispatch(removeItems(this.apparatuses.map(a => a.id)));
        if (this.groupInformation.hasData()) {
            appStore.dispatch(removeFromGroup(this.groupInformation));
        }
    }
    undo() {
        // Add the group first. If the item is added first, it will
        // contain group information referring to a group that does
        // not yet exist.
        if (this.groupInformation.hasData()) {
            appStore.dispatch(addToGroup(this.groupInformation));
        }
        appStore.dispatch(addItemsAtIndices(this.itemsWithIndex));
    }
}
export class UpdateAppearance {
    constructor(id, key, value, previousValue) {
        this.id = id;
        this.key = key;
        this.value = value;
        this.previousValue = previousValue;
    }
    execute() {
        appStore.dispatch(updateAppearance(this.id, this.key, this.value));
    }
    undo() {
        appStore.dispatch(updateAppearance(this.id, this.key, this.previousValue));
    }
}
export class UpdateItemsAppearance {
    constructor(key, values) {
        this.key = key;
        this.values = values;
        this.previousValues = values.map(v => ({ id: v.id, value: v.previousValue }));
    }
    execute() {
        appStore.dispatch(updateItemsAppearance(this.key, this.values));
    }
    undo() {
        appStore.dispatch(updateItemsAppearance(this.key, this.previousValues));
    }
}
/**
 * Update multiple appearance properties on one or multiple items of the same type.
 * Note that this command only makes shallow copies!
 */
export class UpdateAppearanceProperties {
    constructor(id_or_ids, newAppearance, previousAppearance) {
        this.id_or_ids = id_or_ids;
        this.newAppearance = Object.assign({}, newAppearance);
        this.prevAppearance = Object.assign({}, previousAppearance);
    }
    execute() {
        if (Array.isArray(this.id_or_ids)) {
            appStore.dispatch(updateItemsAppearanceProperties(this.id_or_ids, this.newAppearance));
        }
        else {
            appStore.dispatch(updateAppearanceProperties(this.id_or_ids, this.newAppearance));
        }
    }
    undo() {
        if (Array.isArray(this.id_or_ids)) {
            appStore.dispatch(updateItemsAppearanceProperties(this.id_or_ids, this.newAppearance));
        }
        else {
            appStore.dispatch(updateAppearanceProperties(this.id_or_ids, this.prevAppearance));
        }
    }
}
/**
 * Base class for commands that affect multiple apparatus items the same way.
 * Works for apparatus items only!
 */
export class MultiApparatusEditCommand {
    constructor(values) {
        this.valuesMap = {};
        this.previousValuesMap = {};
        for (const value of values) {
            this.valuesMap[value.id.id] = value.value;
            this.previousValuesMap[value.id.id] = value.previousValue;
        }
    }
    /** Do not override. */
    execute() {
        appStore.dispatchFn(this.name, this.mutator(this.valuesMap));
    }
    /** Do not override. */
    undo() {
        appStore.dispatchFn("UpdateLiquid", this.mutator(this.previousValuesMap));
    }
    mutator(mutations) {
        return (store => {
            var newStore = Object.assign({}, store);
            newStore.items = store.items.map(i => mutations[i.id.id] !== undefined
                ? this.mutateItem(i, mutations[i.id.id])
                : i);
            return newStore;
        });
    }
    /** Class factory. */
    static createClass(name, mutateItem) {
        return class _C extends MultiApparatusEditCommand {
            constructor() {
                super(...arguments);
                this.name = "UpdateLiquidAmount";
                this.mutateItem = mutateItem;
            }
        };
    }
}
export const UpdateLiquidAmount = MultiApparatusEditCommand.createClass("UpdateLiquidAmount", (i, value) => i.liquid ? Object.assign(Object.assign({}, i), { liquid: Object.assign(Object.assign({}, i.liquid), { amountRatio: value }) }) : i);
export const UpdateLiquidColor = MultiApparatusEditCommand.createClass("UpdateLiquidColor", (i, value) => i.liquid ? Object.assign(Object.assign({}, i), { liquid: Object.assign(Object.assign({}, i.liquid), { color: value }) }) : i);
export const UpdateLiquidAlpha = MultiApparatusEditCommand.createClass("UpdateLiquidAlpha", (i, value) => i.liquid ? Object.assign(Object.assign({}, i), { liquid: Object.assign(Object.assign({}, i.liquid), { alpha: value }) }) : i);
